/**
 * Bảo vệ khoá luận từ xa có ghi hình (TT 83 C10). Mirror BE `apps/live/defense_views.py`;
 * bắt đầu/kết thúc/vào phòng dùng chung `/live/classes/<live_class_id>/...`.
 */
import { apiClient } from "@/api/client";
import type { JoinTokenResponse } from "@/api/live";

export const DEFENSE_CONSENT_VERSION = "2026-10";
export const DEFENSE_CONSENT_POINTS = [
  "Toàn bộ phiên bảo vệ được ghi hình và ghi âm bắt buộc (Thông tư 56/2026, Điều 12.2) — không tắt được trong phiên.",
  "Bản ghi là hồ sơ tốt nghiệp, lưu trữ tối thiểu 10 năm, có mã băm SHA-256 để chứng minh không bị sửa.",
  "Chỉ thành viên hội đồng và quản trị của trường được xem lại bản ghi; mỗi lần xem đều được ghi nhật ký.",
];

export type CommitteeRole = "chu_tich" | "thu_ky" | "phan_bien" | "uy_vien";
export const ROLE_LABEL: Record<CommitteeRole | "student", string> = {
  chu_tich: "Chủ tịch",
  thu_ky: "Thư ký",
  phan_bien: "Phản biện",
  uy_vien: "Uỷ viên",
  student: "Người bảo vệ",
};
export const RESULT_LABEL: Record<string, string> = {
  "": "Chưa có",
  dat: "Đạt",
  khong_dat: "Không đạt",
};

export interface Defense {
  id: number;
  live_class_id: number;
  title: string;
  thesis_title: string;
  course_code: string;
  scheduled_start: string;
  scheduled_end: string;
  status: "scheduled" | "live" | "ended" | "canceled";
  started_at: string | null;
  ended_at: string | null;
  student: { id: number; name: string; student_id: string };
  committee: { user_id: number; name: string; role: CommitteeRole }[];
  my_role: CommitteeRole | "student" | null;
  consent_given: boolean;
  recording_active: boolean;
  recording_valid: boolean;
  recording_invalid_reason: string;
  result: "" | "dat" | "khong_dat";
  minutes_note: string;
}

export interface DefenseMinutes extends Defense {
  tenant_name: string;
  result_recorded_at: string | null;
  result_recorded_by: string;
  generated_at: string;
  recordings: {
    id: number;
    status: string;
    sha256: string;
    duration_seconds: number | null;
    file_size_bytes: number | null;
    started_at: string | null;
    completed_at: string | null;
    retention_until: string | null;
    legal_hold: boolean;
  }[];
}

export interface DefensePerson {
  id: number;
  name: string;
  email: string;
  student_id: string | null;
}

/** GET /api/live/defenses/people/ — tra cứu người cho form tạo phiên (2026-10-01):
 * `student` = SV của học phần đã chọn; `committee` = giảng viên/quản trị của trường. */
export async function lookupDefensePeople(params: {
  role: "student" | "committee";
  course?: number;
  search?: string;
}): Promise<{ results: DefensePerson[] }> {
  return (await apiClient.get("/live/defenses/people/", { params })).data;
}

export async function listDefenses(): Promise<{ results: Defense[] }> {
  return (await apiClient.get("/live/defenses/")).data;
}
export async function getDefense(id: number): Promise<Defense> {
  return (await apiClient.get(`/live/defenses/${id}/`)).data;
}
export async function createDefense(body: {
  course: number;
  student: number;
  thesis_title: string;
  title?: string;
  scheduled_start: string;
  scheduled_end: string;
  committee: { user: number; role: CommitteeRole }[];
}): Promise<Defense> {
  return (await apiClient.post("/live/defenses/", body)).data;
}
export async function giveDefenseConsent(id: number): Promise<Defense> {
  return (
    await apiClient.post(`/live/defenses/${id}/consent/`, {
      policy_version: DEFENSE_CONSENT_VERSION,
    })
  ).data;
}
export async function submitDefenseFaceMatch(
  id: number,
  selfie: File,
): Promise<{ status: string; reason: string }> {
  const form = new FormData();
  form.append("selfie", selfie);
  return (await apiClient.post(`/live/defenses/${id}/face-match/`, form)).data;
}
export async function saveDefenseResult(
  id: number,
  body: { result: string; minutes_note: string },
): Promise<Defense> {
  return (await apiClient.patch(`/live/defenses/${id}/result/`, body)).data;
}
export async function getDefenseMinutes(id: number): Promise<DefenseMinutes> {
  return (await apiClient.get(`/live/defenses/${id}/minutes/`)).data;
}
export async function startDefense(liveClassId: number) {
  return (await apiClient.post(`/live/classes/${liveClassId}/start/`)).data;
}
export async function endDefense(liveClassId: number) {
  return (await apiClient.post(`/live/classes/${liveClassId}/end/`)).data;
}
export async function restartDefenseRecording(liveClassId: number) {
  return (await apiClient.post(`/live/classes/${liveClassId}/restart-recording/`)).data;
}
export async function joinDefense(liveClassId: number): Promise<JoinTokenResponse> {
  return (await apiClient.post(`/live/classes/${liveClassId}/join-token/`)).data;
}

// ─── Excel: file mẫu / xuất / nhập hàng loạt (2026-10-01) ──────────────────

export async function downloadDefenseTemplate(): Promise<Blob> {
  return (await apiClient.get("/live/defenses/excel-template/", { responseType: "blob" })).data;
}

export async function exportDefenses(): Promise<Blob> {
  return (await apiClient.get("/live/defenses/export/", { responseType: "blob" })).data;
}

/** Có dòng lỗi → 400 `{detail, errors:[{row, error}]}` và KHÔNG tạo phiên nào. */
export async function importDefenses(file: File): Promise<{ created: number }> {
  const form = new FormData();
  form.append("file", file);
  return (await apiClient.post("/live/defenses/import/", form, { timeout: 120_000 })).data;
}
